import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Play, X, ChevronLeft, ChevronRight, Minimize2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import SystemDataService from '../../Services/SystemDataService';
import { ButtonInverted } from '../Buttons/button-component';

const AUTO_INTERVAL = 4000;
const DRAG_THRESHOLD = 80;
const DRAG_NORM = 420;

function getYoutubeId(url = '') {
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&?/]+)/);
    return m?.[1] || '';
}

function getYoutubeThumbnail(url) {
    const id = getYoutubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : '';
}

function getYoutubeEmbed(url) {
    const id = getYoutubeId(url);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : '';
}

function normalizeItem(raw, index) {
    const youtubeUrl = raw.youtubeUrl || raw.url || raw.link || raw.video || '';

    return {
        id: raw.id || index,
        title: raw.title || raw.description || '',
        youtubeUrl,
        thumbnail: raw.thumbnail || raw.image || raw.media || getYoutubeThumbnail(youtubeUrl),
        isVideo: !!getYoutubeId(youtubeUrl),
    };
}

function coverflowStyle(offset, dragProgress, isDragging) {
    const eff = Math.max(-1.4, Math.min(1.4, offset + dragProgress));
    const translateX = eff * 300;
    const rotateY = eff * -16;
    const scale = Math.max(0.8, 1 - Math.abs(eff) * 0.16);
    const opacity = Math.abs(eff) > 1.25 ? 0 : Math.max(0.72, 1 - Math.abs(eff) * 0.25);

    return {
        transform: `perspective(1400px) translate(-50%, -50%) translateX(${translateX}px) rotateY(${rotateY}deg) scale(${scale})`,
        opacity,
        zIndex: Math.round(50 - Math.abs(eff) * 20),
        transition: isDragging ? 'none' : 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease',
    };
}

function VideoModal({ item, onClose, onMinimize, backText = 'חזרה' }) {
    if (!item) return null;

    return createPortal(
        <div className="fixed inset-0 z-[99999] bg-black/80 font-secondary font-extralight backdrop-blur-xl flex items-center justify-center p-3 md:p-8">
            <div className="absolute inset-0" onClick={onClose} />

            <div className="relative z-10 w-full max-w-[1200px]">
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={onMinimize}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold transition-colors"
                    >
                        <Minimize2 className="w-4 h-4" />
                        {backText}
                    </button>

                    <button
                        onClick={onClose}
                        className="w-11 h-11 rounded-full bg-white text-primary-500 flex items-center justify-center shadow-xl hover:scale-105 transition-transform"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="relative overflow-hidden rounded-[24px] md:rounded-[38px] bg-black shadow-[0_40px_120px_rgba(0,0,0,0.55)] border border-white/10">
                    <div className="aspect-video w-full">
                        {item.isVideo ? (
                            <iframe
                                src={getYoutubeEmbed(item.youtubeUrl)}
                                title={item.title}
                                className="w-full h-full"
                                allow="autoplay; encrypted-media; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <img src={item.thumbnail} alt={item.title} className="w-full h-full object-contain" />
                        )}
                    </div>
                </div>

                {item.title && (
                    <h3 className="mt-4 text-center text-white text-xl md:text-3xl font-bold">
                        {item.title}
                    </h3>
                )}
            </div>
        </div>,
        document.body
    );
}

function GallerySection({ gallerySection }) {
    const { dir = 'rtl', language = 'he' } = useSelector((state) => state.systemData || {});
    const title = gallerySection?.title || (language === 'en' ? 'Knowledge is power.' : 'ידע הוא כוח.');
    const subtitle = gallerySection?.subtitle || (language === 'en' ? 'We put it in your hands!' : 'אנחנו שמים אותו בידיים שלכם!');
    const buttonText = gallerySection?.buttonText || (language === 'en' ? 'Visit Knowledge Hub →' : 'למאגר הידע ←');
    const backText = gallerySection?.backText || (language === 'en' ? 'Back' : 'חזרה');

    const [items, setItems] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [modalItem, setModalItem] = useState(null);
    const [paused, setPaused] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const startX = useRef(null);
    const wasDragged = useRef(false);

    useEffect(() => {
        let mounted = true;

        SystemDataService.getSpecialsImages()
            .then((data) => {
                if (!mounted) return;

                const normalized = (data || [])
                    .map(normalizeItem)
                    .filter((item) => item.thumbnail);

                setItems(normalized);
            })
            .catch(() => {});

        return () => {
            mounted = false;
        };
    }, []);

    const prev = () => {
        if (!items.length) return;
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
    };

    const next = () => {
        if (!items.length) return;
        setActiveIndex((i) => (i + 1) % items.length);
    };

    useEffect(() => {
        if (!items.length || paused || modalItem || isDragging) return;

        const interval = setInterval(() => {
            next();
        }, AUTO_INTERVAL);

        return () => clearInterval(interval);
    }, [items.length, paused, modalItem, isDragging]);

    useEffect(() => {
        if (modalItem) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [modalItem]);

    const visibleItems = useMemo(() => {
        if (!items.length) return [];

        return [-1, 0, 1].map((offset) => {
            const index = (activeIndex + offset + items.length) % items.length;
            return { ...items[index], offset, realIndex: index };
        });
    }, [items, activeIndex]);

    const resetDrag = () => {
        setDragOffset(0);
        requestAnimationFrame(() => setIsDragging(false));
    };

    const onPointerDown = (e) => {
        startX.current = e.clientX;
        wasDragged.current = false;
        setIsDragging(true);
    };

    const onPointerMove = (e) => {
        if (startX.current === null) return;

        const delta = e.clientX - startX.current;

        if (Math.abs(delta) > 6) {
            wasDragged.current = true;
        }

        setDragOffset(Math.max(-DRAG_NORM, Math.min(DRAG_NORM, delta)));
    };

    const finishDrag = (clientX) => {
        if (startX.current === null) return;

        const delta = clientX - startX.current;
        startX.current = null;

        if (Math.abs(delta) > DRAG_THRESHOLD) {
            delta < 0 ? next() : prev();
        }

        resetDrag();
    };

    const openItem = (item) => {
        setPaused(true);
        setModalItem(item);
    };

    if (!items.length) return null;

    const dragProgress = dragOffset / DRAG_NORM;

    return (
        <section
            dir={dir}
            className="relative w-full bg-white py-14 px-4 overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="max-w-[1450px] mx-auto bg-secondary-500 rounded-[36px] md:rounded-[72px] px-4 md:px-12 py-10 md:py-14 text-center overflow-hidden">
                <h2 className="text-5xl md:text-7xl font-bold text-primary-500 leading-tight">
                    {title}
                </h2>

                <div className="mt-4 inline-block bg-white px-5 md:px-12 py-2">
                    <h3 className="text-3xl md:text-6xl font-bold text-primary-500 leading-tight">
                        {subtitle}
                    </h3>
                </div>

                <div className="relative mt-10 h-[260px] md:h-[350px] flex items-center justify-center">
                    <button
                        type="button"
                        onClick={prev}
                        className="absolute left-0 md:left-4 z-[100] text-white hover:scale-110 transition-transform"
                    >
                        <ChevronLeft className="w-12 h-12 md:w-16 md:h-16" strokeWidth={4} />
                    </button>

                    <button
                        type="button"
                        onClick={next}
                        className="absolute right-0 md:right-4 z-[100] text-white hover:scale-110 transition-transform"
                    >
                        <ChevronRight className="w-12 h-12 md:w-16 md:h-16" strokeWidth={4} />
                    </button>

                    <div
                        dir="ltr"
                        className="relative w-full h-full cursor-grab active:cursor-grabbing select-none"
                        style={{ touchAction: 'pan-y' }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={(e) => finishDrag(e.clientX)}
                        onPointerCancel={() => {
                            startX.current = null;
                            resetDrag();
                        }}
                    >
                        {visibleItems.map((item) => {
                            const isActive = item.offset === 0;

                            return (
                                <button
                                    type="button"
                                    key={`${item.id}-${item.offset}`}
                                    style={{
                                        position: 'absolute',
                                        left: '50%',
                                        top: '50%',
                                        width: 'min(78vw, 570px)',
                                        height: 'min(50vw, 285px)',
                                        backfaceVisibility: 'hidden',
                                        ...coverflowStyle(item.offset, dragProgress, isDragging),
                                    }}
                                    className="group rounded-[24px] md:rounded-[32px] overflow-hidden bg-white shadow-2xl cursor-pointer"
                                    onClick={() => {
                                        if (wasDragged.current) {
                                            wasDragged.current = false;
                                            return;
                                        }

                                        if (isActive) {
                                            openItem(item);
                                        } else {
                                            setActiveIndex(item.realIndex);
                                        }
                                    }}
                                >
                                    <img
                                        src={item.thumbnail}
                                        alt={item.title}
                                        draggable={false}
                                        className="w-full h-full object-cover pointer-events-none transition-transform duration-500 group-hover:scale-105"
                                    />

                                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors" />

                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className={`${isActive ? 'scale-100 opacity-100' : 'scale-75 opacity-70'} w-14 h-14 md:w-20 md:h-20 rounded-full bg-primary-500/90 text-white flex items-center justify-center shadow-2xl transition-all duration-300 group-hover:scale-110`}>
                                            <Play className="w-7 h-7 md:w-10 md:h-10 translate-x-0.5" fill="currentColor" />
                                        </div>
                                    </div>

                                    {item.title && (
                                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-4">
                                            <p className="text-white text-sm md:text-base font-bold leading-tight line-clamp-2">
                                                {item.title}
                                            </p>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div dir="ltr" className="flex justify-center gap-2 mt-5">
                    {items.map((_, i) => (
                        <button
                            type="button"
                            key={i}
                            onClick={() => setActiveIndex(i)}
                            className={`rounded-full transition-all duration-300 ${
                                i === activeIndex ? 'w-7 h-2 bg-primary-500' : 'w-2 h-2 bg-primary-500/40'
                            }`}
                        />
                    ))}
                </div>

                <div className="mt-8 flex justify-center">
                    <div className="max-w-xs w-full">
                        <ButtonInverted size="lg" href="/blog" className="w-full">
                            {buttonText}
                        </ButtonInverted>
                    </div>
                </div>
            </div>

            {modalItem && (
                <VideoModal
                    item={modalItem}
                    backText={backText}
                    onClose={() => {
                        setModalItem(null);
                        setPaused(false);
                    }}
                    onMinimize={() => setModalItem(null)}
                />
            )}
        </section>
    );
}

export default GallerySection;